import React from "react";
import classNames from "classnames";
import { FinancialDocument } from "@common/types/financialDocument";
import { Scrollbars } from "react-custom-scrollbars-2";
import { Loader } from "@design-system/Loader/Loader";
import { DocumentName } from "../DocumentName";
import { FinancialDocumentExtensionIcon } from "../FinancialDocumentExtensionIcon";
import { useDocumentPreviewData } from "./useDocumentPreviewData";
import styles from "./FinancialDocumentPreview.module.scss";
import { FinancialDocumentCsvPreview } from "./FinancialDocumentCsvPreview";
import { FinancialDocumentXlsxPreview } from "./FinancialDocumentXlsxPreview";

interface Props {
    financialDocument: Pick<FinancialDocument, "id" | "fileName">;
    className?: string;
    compact?: boolean;
    withFileName?: boolean;
    noPadding?: boolean;
    queryOptions?: { retry?: boolean | number };
}

export const FinancialDocumentPreview: React.FC<Props> = ({
    financialDocument,
    className,
    compact,
    withFileName,
    noPadding,
    queryOptions,
}) => {
    const {
        isFetching,
        isError,
        imagePreviewAvailable,
        pdfPreviewAvailable,
        csvPreviewAvailable,
        isCsvTooLarge,
        xlsxPreviewAvailable,
        previewUrl,
        csvData,
        xlsxData,
    } = useDocumentPreviewData(financialDocument, queryOptions);

    const containerClassName = classNames(styles.container, className, {
        [styles.withFileName]: withFileName,
        [styles.compact]: compact,
        [styles.noPadding]: noPadding,
    });

    const fileNameHeader = withFileName && (
        <div className={styles.fileNameHeader}>
            <DocumentName
                fileName={financialDocument.fileName}
                icon
                documentId={financialDocument.id}
            />
        </div>
    );

    if (isFetching) {
        return (
            <div className={containerClassName}>
                {fileNameHeader}
                <Loader />
            </div>
        );
    }

    if (previewUrl) {
        if (imagePreviewAvailable) {
            return (
                <div className={containerClassName}>
                    {fileNameHeader}
                    <img
                        className={styles.preview}
                        src={previewUrl}
                        alt={financialDocument.fileName}
                    />
                </div>
            );
        }

        if (pdfPreviewAvailable) {
            return (
                <div className={containerClassName}>
                    {fileNameHeader}
                    <iframe
                        src={`${previewUrl}#navpanes=0&zoom=page-fit`}
                        title={financialDocument.fileName}
                        className={styles.preview}
                    />
                </div>
            );
        }
    }

    if (isError) {
        return (
            <div className={containerClassName}>
                {fileNameHeader}
                <FinancialDocumentExtensionIcon
                    filename={financialDocument.fileName}
                    className={styles.icon}
                />
                <p className="m-0">File not found for this document</p>
            </div>
        );
    }

    if (isCsvTooLarge) {
        return (
            <div className={containerClassName}>
                {fileNameHeader}
                <FinancialDocumentExtensionIcon
                    filename={financialDocument.fileName}
                    className={styles.icon}
                />
                <p className="m-0">File is too large to preview</p>
            </div>
        );
    }

    if (csvPreviewAvailable && csvData) {
        return (
            <div className={containerClassName}>
                {fileNameHeader}
                <div className={styles.csvPreview}>
                    <Scrollbars height="100%">
                        <FinancialDocumentCsvPreview data={csvData} />
                    </Scrollbars>
                </div>
            </div>
        );
    }

    if (xlsxPreviewAvailable && xlsxData) {
        return (
            <div className={containerClassName}>
                {fileNameHeader}
                <div className={styles.csvPreview}>
                    <Scrollbars height="100%">
                        <FinancialDocumentXlsxPreview
                            sheetsHtml={xlsxData.sheetsHtml}
                            sheetNames={xlsxData.sheetNames}
                            sheetsLimited={xlsxData.sheetsLimited}
                            hasUnevaluatedFormulas={
                                xlsxData.hasUnevaluatedFormulas
                            }
                        />
                    </Scrollbars>
                </div>
            </div>
        );
    }

    return (
        <div className={containerClassName}>
            {fileNameHeader}
            <FinancialDocumentExtensionIcon
                filename={financialDocument.fileName}
                className={styles.icon}
            />
            <p className="m-0">Preview not available</p>
        </div>
    );
};
